/**
 * @file    filterValidation.ts
 * @scope   SHARED
 * @summary Pure predicates deciding whether a single filter is complete (field/operator/values)
 *          plus the helpers to detect and strip incomplete filters from a field's list.
 * @consumes FieldFilter
 * @produces `isFilterFieldMissing`, `isFilterOperatorMissing`, `isFilterValuesMissing`,
 *           `areFiltersInvalid`, `hasIncompleteFilters`, `stripIncompleteFilters`
 * @sideEffects None — pure. Used by the reducer's strip flow and by the field cards.
 * @notes Complements `fieldValidation.ts` (field-level) and `partitionValidation.ts` (partition-level).
 */
import { FieldFilter } from 'models/Enterprise/FieldsManagerModel';

/**
 * Checks whether a filter is missing a field/question selection.
 * @param filter - The filter entry to validate
 * @returns true if no field has been selected
 */
export const isFilterFieldMissing = (filter: FieldFilter): boolean => !filter.filterGuid;

/**
 * Checks whether a filter is missing an operator selection.
 * Only relevant when a field has been selected.
 * @param filter - The filter entry to validate
 * @returns true if a field is selected but no operator has been chosen
 */
export const isFilterOperatorMissing = (filter: FieldFilter): boolean =>
  !!filter.filterGuid && !filter.operator;

/**
 * Checks whether a filter is missing response value selections.
 * Only relevant when a field and operator have been selected.
 * @param filter - The filter entry to validate
 * @returns true if field and operator are set but no values have been chosen
 */
export const isFilterValuesMissing = (filter: FieldFilter): boolean =>
  !!filter.filterGuid &&
  !!filter.operator &&
  (!filter.selectedValues || filter.selectedValues.length === 0);

/**
 * Checks whether a single filter entry is incomplete.
 * A filter is incomplete if ANY of its required fields are missing:
 * - No field/question selected, OR
 * - No operator chosen, OR
 * - No response values selected
 *
 * @param filter - The filter entry to validate
 * @returns true if the filter is missing required configuration
 */
export const isFilterValueMissing = (filter: FieldFilter): boolean =>
  isFilterFieldMissing(filter) || isFilterOperatorMissing(filter) || isFilterValuesMissing(filter);

/**
 * Checks whether any filter in the list has missing required values.
 * This is the filters equivalent of `fieldItemHasMissingRequiredValues`.
 *
 * @param filters - Array of configured filters for a field
 * @returns true if at least one filter is incomplete
 */
export const hasIncompleteFilters = (filters: FieldFilter[]): boolean =>
  filters.some(isFilterValueMissing);

/**
 * Returns a copy of the filters list with the incomplete entries removed.
 *
 * @param filters - Array of configured filters
 * @returns A new array containing only fully configured filters
 */
export const stripIncompleteFilters = (filters: FieldFilter[]): FieldFilter[] =>
  filters.filter((filter) => !isFilterValueMissing(filter));

/**
 * Returns the indices of all incomplete filters in the list.
 * Useful for highlighting specific filter rows that need attention.
 *
 * @param filters - Array of configured filters for a field
 * @returns Set of indices of incomplete filters
 */
export const getIncompleteFilterIndices = (filters: FieldFilter[]): Set<number> => {
  const indices = new Set<number>();
  filters.forEach((filter, index) => {
    if (isFilterValueMissing(filter)) {
      indices.add(index);
    }
  });
  return indices;
};

/**
 * Determines whether a specific filter should be shown as invalid.
 * A filter is invalid when:
 * 1. The parent field has been "touched", AND
 * 2. The filter has missing required values.
 *
 * @param filter - The filter entry to validate
 * @param isTouched - Whether the parent field has been touched by the user
 * @returns true if the filter should display as invalid
 */
export const isFilterInvalid = (filter: FieldFilter, isTouched: boolean): boolean =>
  isTouched && isFilterValueMissing(filter);

/**
 * Determines whether the filters section as a whole should be shown as invalid.
 * Mirrors the logic of `isFieldItemInvalid`: the section is invalid only when:
 * 1. The field has been "touched" (user has interacted with it), AND
 * 2. At least one filter has missing required values.
 *
 * @param filters - Array of configured filters for a field
 * @param isTouched - Whether the parent field has been touched by the user
 * @returns true if filters should display as invalid
 */
export const areFiltersInvalid = (filters: FieldFilter[], isTouched: boolean): boolean =>
  isTouched && hasIncompleteFilters(filters);
